import type {ReactElement} from 'react';
import {useRef} from 'react';
import React from 'react';
import styles from '../../share-panel.module.scss';
import {Text, TextSize} from '@Components/text';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {TagsInputField} from '@Components/tags-input-field';
import type {ShareViaEmailProps} from '@Panels/share-panel/components/share-via-email/share-via-email.types';
import {ShareAccessOptionsDropdown} from '@Panels/share-panel/components/share-access-options-dropdown/share-access-options-dropdown';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {InputFieldState} from '@Components/input-field-v2/input-field.types';
import {TagInputType} from '@Components/tags-input-field/tags-input-field.types';

export function ShareViaEmail({
  permissionLevelLabel,
  accessDropdownOptions,
  onClickInvite,
  popupClassName = '',
  popupSelectorClassName = '',
  inputFieldClassName = '',
  doClosePopupOnInnerClick = false,
  isMobile = false,
  ...props
}: ShareViaEmailProps): ReactElement<any> {
  const inputFieldWrapperRef = useRef<HTMLDivElement>(null);
  
  const isInputInErrorState = (): boolean => {
    return props.inputFieldWrapperState === InputFieldState.ERROR;
  };

  const getInformationText = (): ReactElement<any> | undefined => {
    if (props.inputWrapperInfoText) {
      return (
        <div className={`spacing-m-t-1 flex-row-align-center ${isInputInErrorState() ? 'content-danger' : 'content-sub-text'}`}>
          <Icon icon={isInputInErrorState() ? 'icon-exclamation-triangle' : 'icon-info'} size={IconSize.SIZE_ICON_16} className="spacing-m-r-1" />
          <Text val={props.inputWrapperInfoText ?? ''} size={TextSize.XXSMALL} />
        </div>
      );
    }
    return undefined;
  };

  const getEmailsInputField = (): ReactElement<any> => {
    return (
      <>
        <div className="flex-v-row _full-width">
          <div ref={inputFieldWrapperRef}
            className={`${styles.inputFieldWrapper} _full-width flex-justify-between radius-4 border-s-dark hover-transition-all ${props.inputFieldWrapperState === InputFieldState.ERROR ? styles.errorState : ''}`}
          >
            <div className="flex-1">
              <TagsInputField
                allSuggestedTags={props.allSuggestedTags}
                allSelectedTags={props.allSelectedTags}
                onInputChangeCallback={props.onInputChangeCallback}
                inputClassName={props.inputClassName}
                onSaveTagCallback={props.onSaveTagCallback}
                onPillClick={props.onPillClick}
                onRemoveTag={props.onRemoveTag}
                inputPlaceholder="amy@postermywall.com"
                wrapperClassName={inputFieldClassName}
                tagsLimit={100}
                suggestionPillClassName={styles.suggestionPill}
                suggestionPillIsBoldText={false}
                dropdownClassName="spacing-p-3"
                inputContainerClassName={`_position-static radius-4 ${styles.tagsInputFieldContainer}`}
                showPreviewTags={props.showPreviewTags}
                previewTagsDetail={props.previewTagsDetail}
                doAutofocusOnBlur={false}
                inputFieldState={props.inputFieldWrapperState}
                retainInputOnError={props.retainInputOnError}
                tagInputType={TagInputType.EMAIL}
                inputFieldClassName={styles.tagsInputField}
                inputFieldContainerClassName={styles.tagsInputFieldContainer}
                shouldHideDropdownWhenEmpty
                doAutofocusOnMount
                suggestionsPopoverRef={inputFieldWrapperRef}
                doAutofocusOnSubmit
              />
            </div>
            <ShareAccessOptionsDropdown
              permissionLevelLabel={permissionLevelLabel}
              accessDropdownOptions={accessDropdownOptions}
              popupClassName={popupClassName}
              popupSelectorClassName={popupSelectorClassName}
              doCloseOnInnerClick={doClosePopupOnInnerClick}
              showTooltip={props.showTooltipForOpts}
              tooltipText={props.tooltipText}
              isSelectorDisabled={props.isSelectorDisabled}
            />
          </div>
          {props.inputWrapperInfoText ? getInformationText() : undefined}
        </div>
      </>
    );
  };

  return (
    <div className="flex-v-row">
      <div className="spacing-p-b-1 capitalize-sentence">
        <Text val={window.i18next.t('pmwjs_invite_by_email')} size={TextSize.XSMALL} bold className="content-body" />
      </div>
      <div className={`flex-h-row _full-width flexbox flex-items-start`}>
        {getEmailsInputField()}
        {!isMobile ? (
          <Button
            text={window.i18next.t('pmwjs_invite')}
            size={Size.SMALL}
            icon="icon-group-add"
            iconPlacement={IconPlacement.LEFT}
            type={Type.PRIMARY}
            customClasses={`spacing-m-l-2 ${styles.panelButton}`}
            onClick={onClickInvite}
            isLoading={props.isLoading}
            disabled={props.isInviteButtonDisabled}
          />
        ) : undefined}
      </div>
    </div>
  );
}
